import { Text, View } from "@react-pdf/renderer";
import { styles } from "../ReactPdfStyles";
import { formatDate, renderAsTag, formatResponsiblePerson } from "./utils";
import { getSeverityDescription } from "../../../utils/riskMatrix";
import {
	formatDateDdMmmYyyy,
	formatDateTime,
} from "../../../utils/date_formatter";
import { capitalizeFirstLetter } from "../../../utils/string_convert_helper";

type PotentialRiskType = {
	risk_number: string;
	consequence: string;
	severity_level: string;
	probability: string;
};

type FollowUpActionType = {
	action_number: string;
	action_type: string;
	description: string;
	responsible: string;
	deadline?: string | Date;
	status?: string;
	completed_on?: string | Date;
	closing_comment?: string;
};

type AnalysisAndActionSectionTypes = {
	data: Record<string, any>;
};

export function AnalysisAndActionSection({
	data,
}: AnalysisAndActionSectionTypes) {
	// Probability number → description mapping
	const probabilityDescriptions: Record<string, string> = {
		"1": "0 to 14 days",
		"2": "14 days to 6 months",
		"3": "6 months to 1 year",
		"4": "1-5 years",
		"5": "Less frequent than 5 years",
	};

	const completedByColumnKeys = [
		{ key: "completed_on", label: "Completed on" },
		{ key: "closing_comment", label: "Closing comment" },
	];

	const potentialRiskColumnKeys = [
		{ key: "risk_number", label: "Risk no." },
		{ key: "consequence", label: "Consequence" },
		{ key: "severity_level", label: "Severity level" },
		{ key: "probability", label: "Probability" },
		{ key: "risk", label: "Risk" },
	];

	const causeColumnKeys = [
		{ key: "cause_description", label: "Description" },
		{ key: "human", label: "Human" },
		{ key: "technical", label: "Technical" },
		{ key: "organizational", label: "Organizational" },
	];

	const followUpActionsColumnKeys = [
		{ key: "action_number", label: "Action no." },
		{ key: "action_type", label: "Action type" },
		{ key: "description", label: "Description" },
		{
			key: "remarks",
			label: "Remarks",
			columns: completedByColumnKeys,
		},
	];

	return (
		<View style={[styles.section, styles.tableFontSize]} break>
			<Text style={styles.tableHeader}>Analysis and action</Text>

			<Text style={{ fontWeight: "bold", marginTop: 16, marginBottom: 4 }}>
				• Potential risk
			</Text>
			{/* Potential risk - Header Row */}
			<View style={[styles.row]}>
				{potentialRiskColumnKeys.map((column) => (
					<View
						style={[
							{ fontWeight: "bold", backgroundColor: "#CDCDCD" },
							styles.valueCell,
							styles.borderBottom,
							...(column.key ===
							potentialRiskColumnKeys[potentialRiskColumnKeys.length - 1].key
								? [styles.borderLeft, styles.borderRight]
								: [styles.borderLeft]),
						]}
						key={column.key}
					>
						<Text>{column.label}</Text>
					</View>
				))}
			</View>

			{/* Potential risk - Data Rows (one per entry) */}
			{data.potential_risks &&
				data.potential_risks.length > 0 &&
				data.potential_risks.map((risk: any, idx: number) => (
					<View wrap={false} style={styles.row} key={idx}>
						{potentialRiskColumnKeys.map((column) => {
							const isLastRow = idx === data.potential_risks.length - 1;
							if (!("columns" in column)) {
								const valueMap: Record<string, unknown> = {
									risk_number: risk.risk_number,
									consequence: risk.consequence,
									severity_level: risk.severity_level,
									probability: risk.probability,
									risk: (risk as any).risk,
								};
								// Special styling for "risk" column: color by level
								if (column.key === "risk") {
									const riskText = String(valueMap[column.key] ?? "");
									const level = riskText.split(" - ")[1]?.toUpperCase();
									let cellBgColor: string | undefined = undefined;
									let textColor = "#111111";
									if (level === "HIGH") {
										cellBgColor = "#FDECEC"; // light red
										textColor = "#DC2626"; // red-600
									} else if (level === "MEDIUM") {
										cellBgColor = "#FFF3D9"; // light amber
										textColor = "#F59E0B"; // amber-500
									} else if (level === "LOW") {
										cellBgColor = "#EAF7EE"; // light green
										textColor = "#16A34A"; // green-600
									}

									return (
										<View
											key={column.key}
											style={[
												styles.valueCell,
												...(isLastRow ? [styles.borderBottom] : []),
												...(column.key ===
												potentialRiskColumnKeys[
													potentialRiskColumnKeys.length - 1
												].key
													? [styles.borderLeft, styles.borderRight]
													: [styles.borderLeft]),
												cellBgColor ? { backgroundColor: cellBgColor } : {},
											]}
										>
											<Text style={{ color: textColor, fontWeight: "bold" }}>
												{riskText || ""}
											</Text>
										</View>
									);
								}

								// Render severity level as a tag
								if (column.key === "severity_level") {
									return (
										<View
											key={column.key}
											style={[
												styles.valueCell,
												{ width: "100%", justifyContent: "flex-start" },
												...(isLastRow ? [styles.borderBottom] : []),
												...(column.key ===
												potentialRiskColumnKeys[
													potentialRiskColumnKeys.length - 1
												].key
													? [styles.borderLeft, styles.borderRight]
													: [styles.borderLeft]),
											]}
										>
											<View
												style={{
													flexDirection: "row",
													alignItems: "flex-start",
													width: "100%",
												}}
											>
												{renderAsTag(String(valueMap[column.key] ?? ""))}
												<View style={{ flexGrow: 1, flexBasis: 0 }}>
													<Text wrap>
														{getSeverityDescription(
															"Well Control",
															String(valueMap[column.key] ?? "")
														)}
													</Text>
												</View>
											</View>
										</View>
									);
								}

								// Render probability as a tag
								if (column.key === "probability") {
									return (
										<View
											key={column.key}
											style={[
												styles.valueCell,
												{ justifyContent: "flex-start" },
												...(isLastRow ? [styles.borderBottom] : []),
												...(column.key ===
												potentialRiskColumnKeys[
													potentialRiskColumnKeys.length - 1
												].key
													? [styles.borderLeft, styles.borderRight]
													: [styles.borderLeft]),
											]}
										>
											<View
												style={{
													flexDirection: "row",
													alignItems: "flex-start",
													width: "100%",
												}}
											>
												{renderAsTag(String(valueMap[column.key] ?? ""))}
												<View style={{ flexGrow: 1, flexBasis: 0 }}>
													<Text wrap>
														{probabilityDescriptions[
															String(valueMap[column.key] ?? "")
														] || ""}
													</Text>
												</View>
											</View>
										</View>
									);
								}

								return (
									<View
										key={column.key}
										style={[
											styles.valueCell,
											...(isLastRow ? [styles.borderBottom] : []),
											...(column.key ===
											potentialRiskColumnKeys[
												potentialRiskColumnKeys.length - 1
											].key
												? [styles.borderLeft, styles.borderRight]
												: [styles.borderLeft]),
										]}
									>
										<Text>{String(valueMap[column.key])}</Text>
									</View>
								);
							}
						})}
					</View>
				))}

			<Text style={{ fontWeight: "bold", marginTop: 16, marginBottom: 4 }}>
				• Cause
			</Text>
			{/* Cause - Header Row */}
			<View style={[styles.row]}>
				{causeColumnKeys.map((column) => (
					<View
						style={[
							{ fontWeight: "bold", backgroundColor: "#CDCDCD" },
							styles.valueCell,
							styles.borderBottom,
							...(column.key === causeColumnKeys[causeColumnKeys.length - 1].key
								? [styles.borderLeft, styles.borderRight]
								: [styles.borderLeft]),
						]}
						key={column.key}
					>
						<Text>{column.label}</Text>
					</View>
				))}
			</View>

			{/* Cause - Data Rows (one per entry) */}
			{data.causes &&
				data.causes.length > 0 &&
				data.causes.map((cause: any, idx: number) => (
					<View wrap={false} style={styles.row} key={idx}>
						{causeColumnKeys.map((column) => {
							const valueMap: Record<string, unknown> = {
								cause_description: cause.cause_description,
								human: cause.human_category_name,
								technical: cause.technical_category_name,
								organizational: cause.organisational_category_name,
							};
							const raw = valueMap[column.key];
							const display = Array.isArray(raw)
								? (raw as any[]).filter(Boolean).join(", ")
								: String(raw ?? "").replace(/,\s*/g, ", ");
							const isLastRow = idx === data.causes.length - 1;
							return (
								<View
									key={column.key}
									style={[
										styles.valueCell,
										...(isLastRow ? [styles.borderBottom] : []),
										...(column.key ===
										causeColumnKeys[causeColumnKeys.length - 1].key
											? [styles.borderLeft, styles.borderRight]
											: [styles.borderLeft]),
									]}
								>
									<Text>{display}</Text>
								</View>
							);
						})}
					</View>
				))}

			<Text style={{ fontWeight: "bold", marginTop: 16, marginBottom: 4 }}>
				• Actions
			</Text>
			{/* Actions - Header Row */}
			<View style={[styles.row]}>
				{followUpActionsColumnKeys.map((column) => (
					<View
						style={[
							{ fontWeight: "bold", backgroundColor: "#CDCDCD" },
							styles.valueCell,
							...(column.key ===
							followUpActionsColumnKeys[followUpActionsColumnKeys.length - 1]
								.key
								? [styles.borderLeft, styles.borderRight]
								: [styles.borderLeft]),
						]}
						key={column.key}
					>
						<Text>{column.label}</Text>
					</View>
				))}
			</View>

			{/* Actions - Data Rows (one per entry) */}
			{data.follow_up_actions &&
				data.follow_up_actions.length > 0 &&
				data.follow_up_actions.map((action: any, idx: number) => {
					const responsibleDisplay = formatResponsiblePerson(
						action?.responsible,
						action?.responsible_name
					);

					const closingComment = action.closing_comment || "";

					const deadline = new Date(action?.deadline);
					const completedOn = new Date(action?.completed_on);

					// Strip time (set to 00:00:00)
					deadline.setHours(0, 0, 0, 0);
					completedOn.setHours(0, 0, 0, 0);

					const isOverdue = deadline < completedOn;

					return (
						<View wrap={false} style={styles.row} key={idx}>
							{followUpActionsColumnKeys.map((column) => {
								if (column.key === "remarks") {
									return (
										<View
											key={column.key}
											style={[
												styles.valueCell,
												styles.borderLeft,
												styles.borderRight,
												{ flexDirection: "column", gap: 4, lineHeight: 0.8 },
											]}
										>
											{responsibleDisplay && (
												<View style={{ marginBottom: 8 }}>
													<Text
														style={{
															fontWeight: "bold",
														}}
													>
														Person Responsible:
													</Text>
													<Text>{responsibleDisplay}</Text>
												</View>
											)}
											{action.deadline && (
												<View style={{ marginBottom: 8 }}>
													<Text style={{ fontWeight: "bold" }}>Deadline:</Text>
													<Text style={isOverdue ? { color: "red" } : {}}>
														{formatDateDdMmmYyyy(action.deadline)}
													</Text>
												</View>
											)}

											{action.completed_on && (
												<View style={{ marginBottom: 8 }}>
													{responsibleDisplay ? (
														<>
															<Text style={{ fontWeight: "bold" }}>
																Completed by:{" "}
															</Text>
															{isOverdue && (
																<Text style={{ color: "red" }}>[Overdue]</Text>
															)}
															<Text>
																{responsibleDisplay} on{" "}
																{formatDateTime(action.completed_on)}
															</Text>
														</>
													) : (
														<Text>
															Completed on {formatDateTime(action.completed_on)}
														</Text>
													)}
												</View>
											)}
											{!action.completed_on && (
												<View style={{ marginBottom: 8 }}>
													<Text style={{ fontStyle: "italic" }}>
														Pending completion
													</Text>
												</View>
											)}

											{action.closing_comment && (
												<>
													<Text style={{ fontWeight: "bold" }}>
														Closing comment:
													</Text>
													<Text>{closingComment}</Text>
												</>
											)}
										</View>
									);
								}

								const valueMap: Record<string, unknown> = {
									action_number: action.action_number,
									action_type: capitalizeFirstLetter(action.action_type ?? ""),
									description: action.description,
								};

								return (
									<View
										key={column.key}
										style={[
											styles.valueCell,
											...(column.key ===
											followUpActionsColumnKeys[
												followUpActionsColumnKeys.length - 1
											].key
												? [styles.borderLeft, styles.borderRight]
												: [styles.borderLeft]),
										]}
									>
										<Text>{String(valueMap[column.key] ?? "")}</Text>
									</View>
								);
							})}
						</View>
					);
				})}
		</View>
	);
}

export default AnalysisAndActionSection;
